import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import {
  BackButton,
  Box,
  Center,
  FloatingContainer,
  Row,
  Screen,
  Stepper,
  Text,
} from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog'; // cspell:ignore posthog
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { CreatePermanentJobPostingSteps, TCreatePermanentJobPostingPageRoutes } from './Types';
import { useCurrentPermanentJobPostingSlug, useDraftJobPost } from './hooks';

export const CreatePermanentJobsPage = () => {
  const location = useLocation();
  const slug = useCurrentPermanentJobPostingSlug();
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const navigate = useNavigate();

  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const { draft, isLoading } = useDraftJobPost(id);

  useEffect(() => {
    if (isLoading || !draft) return;

    if (draft.status !== JobPostingStatus.Draft) {
      navigate('/', { replace: true });
    }
  }, [draft, isLoading, navigate]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  const steps = useMemo(() => {
    if (isEnforcePostAJobFunnelImprovement) {
      return ['Job Information', 'Work Details', 'Job Description', 'Review & Payment'];
    }
    return ['Job Details', 'Review and Payment'];
  }, [isEnforcePostAJobFunnelImprovement]);

  const currentStepIndex = useMemo(() => {
    if (isEnforcePostAJobFunnelImprovement) {
      switch (slug) {
        case CreatePermanentJobPostingSteps.BasicInformation:
          return 0;
        case CreatePermanentJobPostingSteps.WorkDetails:
          return 1;
        case CreatePermanentJobPostingSteps.JobDescription:
          return 2;
        case CreatePermanentJobPostingSteps.ReviewAndPayment:
          return 3;
        default:
          return 0;
      }
    }

    if (slug === CreatePermanentJobPostingSteps.ReviewAndPayment) {
      return 1;
    }

    return 0;
  }, [slug, isEnforcePostAJobFunnelImprovement]);

  const stepperWidth = isEnforcePostAJobFunnelImprovement ? 700 : 360;

  return (
    <>
      <Screen>
        <FloatingContainer flex={1} borderRadius={4} paddingBlock={48} paddingInline={16}>
          <Row
            position="relative"
            alignItems="center"
            justifyContent="center"
            marginBottom={isEnforcePostAJobFunnelImprovement ? 22 : 0}
          >
            <Box position="absolute" left={0}>
              <BackButton />
            </Box>
            {isEnforcePostAJobFunnelImprovement ? (
              <Center width={stepperWidth}>
                <Stepper currentStep={currentStepIndex} steps={steps} />
              </Center>
            ) : (
              <Text bold fontSize={32} textAlign="center">
                Post a Job
              </Text>
            )}
          </Row>
          {!isEnforcePostAJobFunnelImprovement && (
            <Center marginTop={24} width={360} marginBottom={40}>
              <Stepper currentStep={currentStepIndex} steps={steps} />
            </Center>
          )}
          <Outlet />
        </FloatingContainer>
      </Screen>
    </>
  );
};
